"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
type Stat = {
  value: string;
  prefix: string;
  number: string;
  suffix: string;
  label: string;
  descriptor: string;
  wide: boolean;
  trend: number[];
};
const stats: Stat[] = [{
  value: "#1",
  prefix: "#",
  number: "1",
  suffix: "",
  label: "AGENCY RANKING / 2024",
  descriptor: "Recognized as the premier partner for executive search in high-growth sectors.",
  wide: false,
  trend: []
}, {
  value: "98%",
  prefix: "",
  number: "98",
  suffix: "%",
  label: "RETENTION RATE",
  descriptor: "Candidates placed by Nexus remain in their roles for over three years.",
  wide: false,
  trend: []
}, {
  value: "$2B",
  prefix: "$",
  number: "2",
  suffix: "B",
  label: "TOTAL SALARY PLACED",
  descriptor: "Driving economic growth through strategic talent acquisition across global markets.",
  wide: false,
  trend: []
}, {
  value: "15:1",
  prefix: "",
  number: "15",
  suffix: ":1",
  label: "CANDIDATE : ROLE RATIO",
  descriptor: "Curated shortlists ensuring precision matching for every unique corporate need.",
  wide: false,
  trend: []
}, {
  value: "412",
  prefix: "",
  number: "412",
  suffix: "",
  label: "EXECUTIVE PLACEMENTS / 2024",
  descriptor: "A decade of consistent growth in placing leadership talent at Fortune 500 firms.",
  wide: true,
  trend: [112, 134, 151, 168, 175, 192, 204, 219, 231, 412]
}];
const CHARSET = "0123456789#$%:B";
function ScrambleNumber({
  finalValue,
  isActive
}: {
  finalValue: string;
  isActive: boolean;
}) {
  const [display, setDisplay] = useState<string>(() => finalValue.split("").map(c => /[0-9]/.test(c) ? "0" : c).join(""));
  useEffect(() => {
    if (!isActive) return;
    const chars = finalValue.split("");
    const totalSteps = 28;
    let step = 0;
    const interval = setInterval(() => {
      step += 1;
      const next = chars.map((c, i) => {
        if (!/[0-9]/.test(c)) return c;
        const lockAt = Math.floor((i + 1) / chars.length * totalSteps);
        if (step >= lockAt) return c;
        return CHARSET[Math.floor(Math.random() * 10)];
      }).join("");
      setDisplay(next);
      if (step >= totalSteps) {
        setDisplay(finalValue);
        clearInterval(interval);
      }
    }, 55);
    return () => clearInterval(interval);
  }, [finalValue, isActive]);
  const chars = display.split("");
  return <span className="font-serif tabular-nums">
      {chars.map((c, i) => <span key={i} data-index={i} className="text-primary !text-8xl">
          {c}
        </span>)}
    </span>;
}
function Sparkline({
  data
}: {
  data: number[];
}) {
  const width = 180;
  const height = 40;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data.map((v, i) => `${i * stepX},${height - (v - min) / range * height}`).join(" ");
  return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible" aria-hidden="true">
      <polyline points={points} fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {data.map((v, i) => <circle key={i} data-index={i} cx={i * stepX} cy={height - (v - min) / range * height} r={i === data.length - 1 ? 2.5 : 1} fill={i === data.length - 1 ? "var(--primary)" : "var(--muted-foreground)"} />)}
    </svg>;
}
export default function OurGlobalImpact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="537" id="our-global-impact" ref={ref} className="dark relative bg-background text-foreground overflow-hidden py-32 md:py-40 lg:py-[200px]">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-1/3 opacity-[0.07]" style={{
      maskImage: "linear-gradient(to left, var(--foreground) 0%, transparent 100%)",
      WebkitMaskImage: "linear-gradient(to left, var(--foreground) 0%, transparent 100%)"
    }}>
        <svg width="100%" height="100%" viewBox="0 0 400 800" preserveAspectRatio="xMidYMid slice">
          <g stroke="currentColor" strokeWidth="0.5" fill="none">
            <polygon points="50,30 120,10 180,60 140,140 60,120" />
            <polygon points="180,60 260,40 310,110 260,180 140,140" />
            <polygon points="260,40 360,30 400,90 310,110" />
            <polygon points="60,120 140,140 130,220 40,210" />
            <polygon points="140,140 260,180 240,260 130,220" />
            <polygon points="260,180 310,110 400,180 360,260 240,260" />
            <polygon points="40,210 130,220 110,310 30,300" />
            <polygon points="130,220 240,260 220,340 110,310" />
            <polygon points="240,260 360,260 380,350 220,340" />
            <polygon points="30,300 110,310 90,400 20,390" />
            <polygon points="110,310 220,340 200,420 90,400" />
            <polygon points="220,340 380,350 400,440 200,420" />
            <polygon points="20,390 90,400 80,490 10,480" />
            <polygon points="90,400 200,420 190,510 80,490" />
            <polygon points="200,420 400,440 380,540 190,510" />
            <polygon points="10,480 80,490 70,580 0,570" />
            <polygon points="80,490 190,510 170,600 70,580" />
            <polygon points="190,510 380,540 360,640 170,600" />
            <polygon points="0,570 70,580 60,680 0,680" />
            <polygon points="70,580 170,600 150,700 60,680" />
            <polygon points="170,600 360,640 340,740 150,700" />
            <polygon points="60,680 150,700 130,790 50,790" />
            <polygon points="150,700 340,740 320,800 130,790" />
          </g>
        </svg>
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-6">
          {}
          <div className="col-span-12 lg:col-start-2 lg:col-span-10">
            <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.18em] text-primary">
              <span aria-hidden="true"></span>
              <span>02 -- Nexus Performance</span>
            </div>
          </div>

          {}
          <h2 className="col-span-12 lg:col-start-2 lg:col-span-9 mt-8 font-serif font-default text-4xl md:text-6xl lg:text-[72px] leading-[1.02] tracking-tight text-foreground">
            Proven results for elite organizations.
          </h2>

          {}
          <div className="col-span-12 lg:col-start-2 lg:col-span-10 mt-14">
            <Separator className="bg-border" />
          </div>

          {}
          <div className="col-span-12 lg:col-start-2 lg:col-span-10 mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border">
              {stats.filter(s => !s.wide).map((stat, i) => <div key={stat.label} data-index={i} className="group relative px-0 md:px-6 lg:px-8 py-10 md:py-12 first:pl-0 last:pr-0 transition-opacity duration-700" style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(20px)",
              transition: `opacity 700ms ease-out ${i * 120}ms, transform 700ms ease-out ${i * 120}ms`
            }}>
                    <div className="font-serif text-[88px] md:text-[104px] lg:text-[120px] leading-[0.95] tracking-tight">
                      <ScrambleNumber finalValue={stat.value} isActive={isVisible} />
                    </div>
                    <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-foreground">
                      {stat.label}
                    </div>
                    <p className="mt-3 max-w-[28ch] font-default text-sm leading-relaxed text-muted-foreground">
                      {stat.descriptor}
                    </p>
                  </div>)}
            </div>
          </div>

          {}
          <div className="col-span-12 lg:col-start-2 lg:col-span-10 mt-0">
            <Separator className="bg-border" />
            {stats.filter(s => s.wide).map((stat, i) => <div key={stat.label} data-index={i} className="grid grid-cols-1 md:grid-cols-12 gap-8 py-12" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(20px)",
            transition: `opacity 700ms ease-out 520ms, transform 700ms ease-out 520ms`
          }}>
                  <div className="md:col-span-4">
                    <div className="font-serif text-[88px] md:text-[104px] lg:text-[120px] leading-[0.95] tracking-tight">
                      <ScrambleNumber finalValue={stat.value} isActive={isVisible} />
                    </div>
                  </div>
                  <div className="md:col-span-5 flex flex-col justify-end">
                    <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-foreground">
                      {stat.label}
                    </div>
                    <p className="mt-3 max-w-[44ch] font-default text-sm leading-relaxed text-muted-foreground">
                      {stat.descriptor}
                    </p>
                  </div>
                  <div className="md:col-span-3 flex flex-col justify-end items-start md:items-end">
                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">
                      10-YR TREND
                    </div>
                    <Sparkline data={stat.trend} />
                    <div className="mt-2 flex w-full max-w-[180px] justify-between font-mono text-[10px] text-muted-foreground">
                      <span>2015</span>
                      <span>2024</span>
                    </div>
                  </div>
                </div>)}
          </div>

          {}
          <div className="col-span-12 lg:col-start-2 lg:col-span-10 mt-4">
            <div className="h-px w-24 bg-primary" />
            <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              <span className="text-primary">[</span>
              <span className="px-1">SOURCES: INTERNAL AUDIT / MARKET ANALYSIS 2026</span>
              <span className="text-primary">]</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}